"use client";

import { useState, useEffect } from "react";
import { auth, db } from "@/lib/firebase";
import { onAuthStateChanged, User } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";

export interface AuthUser {
  uid: string;
  email: string | null;
  displayName: string | null;
  role: "student" | "admin" | "superadmin";
}

export function useAuth() {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          // Fetch user role and name from Firestore
          const userDocRef = doc(db, "users", firebaseUser.uid);
          const userDoc = await getDoc(userDocRef);
          
          let role: AuthUser["role"] = "student";
          let dbDisplayName: string | null = firebaseUser.displayName;
          
          if (userDoc.exists()) {
            const userData = userDoc.data();
            role = userData.role || "student";
            // If auth displayName is missing, use name from firestore
            if (!dbDisplayName) {
              dbDisplayName = userData.name || userData.displayName || null;
            }
          }

          setUser({
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            displayName: dbDisplayName,
            role: role,
          });
        } catch (error) {
          console.error("Auth state synchronization error:", error);
          setUser({
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            displayName: firebaseUser.displayName,
            role: "student",
          });
        }
      } else {
        setUser(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  return { user, loading };
}
